import React, { ComponentPropsWithRef, useEffect, useState } from 'react';
import { MdCancel, MdSearch } from 'react-icons/md';
import { useAsyncDebounce } from 'react-table';
import classNames from 'classnames';

import { Icon } from '../icon';
import { getInputClassName, InputClassProps } from '../input-class-name.util';

export type SearchInputProps = {
  showClearButton?: boolean;
  debounceTime?: number;
  className?: string;
  onChange: (value: string) => void;
} & Omit<ComponentPropsWithRef<'input'>, 'onChange'> &
  InputClassProps;

export const SearchInput = ({
  value: valueProp,
  name,
  placeholder = 'Search',
  showClearButton = true,
  debounceTime = 500,
  height = 'md',
  hasError,
  disabled,
  fullWidth,
  variant,
  className,
  onChange,
  ...rest
}: SearchInputProps) => {
  const [value, setValue] = useState(valueProp);

  const onSearchChange = useAsyncDebounce((search) => {
    onChange(search);
  }, debounceTime);

  useEffect(() => {
    setValue(valueProp);
  }, [valueProp]);

  return (
    <div className="relative inline-flex items-center">
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        name={name}
        aria-invalid={hasError || undefined}
        className={classNames(
          getInputClassName({ variant, disabled, hasError, fullWidth, height }),
          {
            'px-12': height === 'md' && showClearButton,
            'pl-12': height === 'md' && !showClearButton,
            'px-10': height === 'sm' && showClearButton,
            'pl-10': height === 'sm' && !showClearButton,
          },
          className
        )}
        onChange={({ target: { value } }) => {
          setValue(value);
          onSearchChange(value);
        }}
        {...rest}
      />

      <Icon
        size={height === 'md' ? '24' : '16'}
        icon={
          <MdSearch
            className={classNames(
              'absolute text-primary-300 pointer-events-none',
              {
                'ml-4': height === 'md',
                'ml-3': height === 'sm',
              }
            )}
          />
        }
      />

      {showClearButton && value && (
        <button
          type="button"
          tabIndex={-1}
          className="absolute right-0 mr-2 p-1 text-primary-400 hover:text-primary-500 focus:outline-none"
          onClick={() => {
            setValue('');
            onChange('');
          }}
        >
          <Icon icon={<MdCancel />} />
        </button>
      )}
    </div>
  );
};
